import { Component, OnInit, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { EcmService } from '../../../ecm.service';
import { EcmPartsService } from '../ecm-parts.service';
import { ToastrService } from 'ngx-toastr';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'ecm-parts-part-change',
  templateUrl: './ecm-parts-part-change.component.html',
  styleUrls: ['./ecm-parts-part-change.component.scss']
})
export class EcmPartsPartChangeComponent implements OnInit {

  private readonly design: string[] = ['designDetail', 'designAsm', 'designItac', 'designEtac', 'designHaf'];
  private readonly gdt: string[] = ['gdtDetail', 'gdtAsm', 'gdtItac', 'gdtEtac', 'gdtHaf'];

  partId: number;
  partChangeId: number;
  toolTypeList: string;

  authTypes: string[] = [];

  partChangeForm = new UntypedFormGroup({
    releaseLevel: new UntypedFormControl(null, [Validators.required, Validators.maxLength(8)]),
    approvalDate: new UntypedFormControl(null, [Validators.required]),
    authNumber: new UntypedFormControl(null, Validators.maxLength(20)),
    authNumberN: new UntypedFormControl(null, [Validators.required, Validators.maxLength(20)]),
    authTypeName: new UntypedFormControl(null, [Validators.required]),
    pdi: new UntypedFormControl(null, [Validators.maxLength(8)]),
    affectsDesign: new UntypedFormControl(null),
    designDetail: new UntypedFormControl(false),
    designAsm: new UntypedFormControl(false),
    designItac: new UntypedFormControl(false),
    designEtac: new UntypedFormControl(false),
    designHaf: new UntypedFormControl(false),
    affectsGdt: new UntypedFormControl(null),
    gdtDetail: new UntypedFormControl(false),
    gdtAsm: new UntypedFormControl(false),
    gdtItac: new UntypedFormControl(false),
    gdtEtac: new UntypedFormControl(false),
    gdtHaf: new UntypedFormControl(false),
    gdtFileName: new UntypedFormControl(null, Validators.maxLength(100)),
    buildEvent: new UntypedFormControl(null),
    reqdForNec: new UntypedFormControl(1, [Validators.required]),
  }, {
    updateOn: 'blur',
    validators: (group: UntypedFormGroup) => {
      const authNumber = group.get('authNumber');
      const authTypeName = group.get('authTypeName').value;
      if (authNumber.value) {
        if (authTypeName === 'AVO') {
          authNumber.setErrors({ avoType: true });
        } else {
          authNumber.setErrors(null);
        }
      } else if (authTypeName !== 'AVO') {
        authNumber.setErrors({ required: true });
      } else {
        authNumber.setErrors(null);
      }

      return {};
    }
  });

  constructor(
    private readonly dialogRef: MatDialogRef<EcmPartsPartChangeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly ecmService: EcmService,
    private readonly ecmPartsService: EcmPartsService,
    private readonly toastr: ToastrService
  ) { }

  ngOnInit() {
    if (!this.data) {
      this.toastr.error('Please select a part to change.');
      return;
    }

    this.partId = this.data.partId;
    this.partChangeId = this.data.partChangeId;
    this.toolTypeList = this.data.toolTypeList;

    // Populate form controls
    for (const [key, value] of Object.entries(this.data)) {
      const control: AbstractControl = this.partChangeForm.get(key);
      if (value !== null && control) {
        control.setValue(value);
      }
    }

    if (this.data.approvalDate) {
      this.partChangeForm.get('approvalDate').setValue(new Date(this.data.approvalDate));
    }

    this.affectsDesignChanged(this.data.affectsDesign || 0);
    this.affectsGdtChanged(this.data.affectsGdt || 0);


    this.ecmService.getAuthTypeCriteria()
      .subscribe(response => this.authTypes = response.map(type => type.authTypeName));
  }

  private updateCheckboxSelectors(boxes: any[], value: boolean) {
    boxes.forEach((design: string, index: number) => {
      const enabled = this.toolTypeList.charAt(index) === '1' && value;
      const control: AbstractControl = this.partChangeForm.get(design);
      if (enabled) {
        control.enable();
      } else {
        control.setValue(false);
        control.disable();
      }
    });
  }

  affectsDesignChanged(value = 0) {
    this.updateCheckboxSelectors(this.design, value === 1);
  }

  affectsGdtChanged(value = 0) {
    this.updateCheckboxSelectors(this.gdt, value === 1);
  }

  private setupBody(body) {
    body.authNumber = (body.authNumber || '').trim() === 'N/A' ? 'N/AVAIL' : body.authNumber;
    body.authNumberN = (body.authNumberN || '').trim() === 'N/A' ? 'N/AVAIL' : body.authNumberN;
    body.pdi = (body.pdi || '').trim() === 'NA' ? 'N/A' : body.pdi;

    body.partId = this.partId;
    body.partChangeId = this.partChangeId;

    body.releaseLevel = body.releaseLevel.toUpperCase();
    body.authNumberN = body.authNumberN.toUpperCase();
    if (body.authNumber != null) {
      body.authNumber = body.authNumber.toUpperCase();
    }
    if (body.pdi != null) {
      body.pdi = body.pdi.toUpperCase();
    }
    if (body.gdtFileName != null) {
      body.gdtFileName = body.gdtFileName.toUpperCase();
    }
    if (body.buildEvent != null) {
      body.buildEvent = body.buildEvent.toUpperCase();
    }
  }

  save() {
    if (!this.partChangeForm.valid) {
      this.toastr.error('Please fill in all required forms.');
      return;
    }

    const body = this.partChangeForm.value;

    for (const key in body) {
      if (typeof body[key] === 'boolean') {
        body[key] = body[key] ? 1 : 0;
      }
    }

    this.setupBody(body);

    const message = body.partChangeId ? 'Part Change successfully updated!' : 'Part Change successfully created!';

    (body.partChangeId ?
      this.ecmPartsService.modifyPartChange.bind(this.ecmPartsService) :
      this.ecmPartsService.createPartChange.bind(this.ecmPartsService)
    )(body).subscribe(
      () => {
        this.toastr.success(message);
        this.dialogRef.close(this.data.partChangeId);
      }
    );
  }
}
